import type { RentalBoard, RentalRow } from './api';

/** The board's sections, in the order the counter reads them. */
export type SectionKey = keyof Omit<RentalBoard, 'today'>;

/** What the status chips (and the overview cards) narrow the board to. */
export type BoardStatus = 'overdue' | 'pickups' | 'returns' | 'ships' | 'out';

export const BOARD_STATUSES = [
  'overdue',
  'pickups',
  'returns',
  'ships',
  'out',
] as const satisfies readonly BoardStatus[];

/** The board sections each status chip keeps. `out` = everything that is with a customer right now. */
export const STATUS_SECTIONS: Record<BoardStatus, readonly SectionKey[]> = {
  overdue: ['overdue'],
  pickups: ['pickups', 'missed'],
  returns: ['returns', 'arrivals'],
  ships: ['ships', 'lateShips'],
  out: ['out', 'returns', 'arrivals', 'overdue'],
};

export type BoardFilter = {
  /** typed search: every word must appear somewhere in the row */
  q: string;
  /** a catalogue category id */
  cat?: string;
  st?: BoardStatus;
};

/** A rental's catalogue category, by the item it rents (the board row carries only the item id). */
export type CategoryOf = (serviceId: string) => { id: string; name: string } | undefined;

const SECTION_KEYS: readonly SectionKey[] = [
  'overdue',
  'returns',
  'arrivals',
  'lateShips',
  'ships',
  'pickups',
  'missed',
  'out',
  'upcoming',
];

export const isFiltered = (f: BoardFilter) => f.q.trim() !== '' || f.cat !== undefined || f.st !== undefined;

/**
 * The category of the address, if it can narrow anything: the catalogue must have loaded (`known` = the category of
 * every item, undefined while loading or without the right to read it) and must hold it. Otherwise no row could match
 * and a link back to the board (browser Back, a refresh, an old link) would show it empty.
 */
export const usableCategory = (
  cat: string | undefined,
  known: readonly (string | null)[] | undefined,
): string | undefined => (cat !== undefined && known?.includes(cat) ? cat : undefined);

/** Item name and code, customer name and code, and the notes (where an old order number is usually kept). */
function haystack(r: RentalRow): string {
  const a = r.appointment;
  return [a.serviceName, r.itemCode, a.customerName, a.customer?.code, r.note, a.note]
    .filter((s): s is string => Boolean(s))
    .join('\n')
    .toLowerCase();
}

const PHONE_LIKE = /^[\d\s\-().+]+$/;
/** Fewer digits than this are an item number or a quantity, not a phone. */
const MIN_PHONE_DIGITS = 3;

/** A phone as digits only, written both ways a Thai number is: 0812345678 and 66812345678 (+66 81 234 5678). */
function phoneForms(phone: string | null): string[] {
  const d = phone?.replace(/\D/g, '') ?? '';
  if (d === '') return [];
  return [d.startsWith('66') ? `0${d.slice(2)}` : d, d.startsWith('0') ? `66${d.slice(1)}` : d];
}

/**
 * Every typed word must be found in the row. Digits typed like a phone ("081-234 5678", "+66 81 234") match the
 * customer's number whatever spaces, dashes or country code either side uses.
 */
export function matchesText(r: RentalRow, q: string): boolean {
  const whole = q.trim();
  if (whole === '') return true;
  const text = haystack(r);
  const forms = phoneForms(r.customerPhone);
  const inPhone = (s: string) => {
    const d = s.replace(/\D/g, '');
    return PHONE_LIKE.test(s) && d.length >= MIN_PHONE_DIGITS && forms.some((f) => f.includes(d));
  };
  // a number typed with spaces or dashes is one number, not several words
  if (inPhone(whole)) return true;
  return whole
    .toLowerCase()
    .split(/\s+/)
    .every((w) => text.includes(w) || inPhone(w));
}

const inCategory = (r: RentalRow, cat: string | undefined, categoryOf: CategoryOf) =>
  cat === undefined || categoryOf(r.appointment.serviceId)?.id === cat;

/** The board with the filter applied to every section (sections keep their order; empty ones are dropped by the page). */
export function filterBoard(
  board: RentalBoard,
  f: BoardFilter,
  categoryOf: CategoryOf,
): Record<SectionKey, RentalRow[]> {
  const keep = f.st ? STATUS_SECTIONS[f.st] : null;
  const out = {} as Record<SectionKey, RentalRow[]>;
  for (const key of SECTION_KEYS) {
    out[key] =
      keep && !keep.includes(key)
        ? []
        : board[key].filter((r) => matchesText(r, f.q) && inCategory(r, f.cat, categoryOf));
  }
  return out;
}

/** How many rows each status chip would show under the current search and category. */
export function statusCounts(
  board: RentalBoard,
  f: BoardFilter,
  categoryOf: CategoryOf,
): Record<BoardStatus, number> {
  const base = filterBoard(board, { q: f.q, cat: f.cat }, categoryOf);
  const count = (st: BoardStatus) => STATUS_SECTIONS[st].reduce((n, k) => n + base[k].length, 0);
  return {
    overdue: count('overdue'),
    pickups: count('pickups'),
    returns: count('returns'),
    ships: count('ships'),
    out: count('out'),
  };
}

/** How many rows each catalogue category would show under the current search and status, biggest first. */
export function categoryCounts(
  board: RentalBoard,
  f: BoardFilter,
  categoryOf: CategoryOf,
): { id: string; name: string; count: number }[] {
  const base = filterBoard(board, { q: f.q, st: f.st }, categoryOf);
  const seen = new Map<string, { id: string; name: string; count: number }>();
  for (const key of SECTION_KEYS)
    for (const r of base[key]) {
      const c = categoryOf(r.appointment.serviceId);
      if (!c) continue;
      const have = seen.get(c.id);
      if (have) have.count++;
      else seen.set(c.id, { ...c, count: 1 });
    }
  // a category picked earlier stays on the bar even when the other filters leave it empty
  if (f.cat && !seen.has(f.cat)) {
    for (const key of SECTION_KEYS) {
      const c = board[key].map((r) => categoryOf(r.appointment.serviceId)).find((x) => x?.id === f.cat);
      if (c) {
        seen.set(c.id, { ...c, count: 0 });
        break;
      }
    }
  }
  return [...seen.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'th'));
}

export const boardTotal = (board: RentalBoard) => SECTION_KEYS.reduce((n, k) => n + board[k].length, 0);
export const sectionsTotal = (sections: Record<SectionKey, RentalRow[]>) =>
  SECTION_KEYS.reduce((n, k) => n + sections[k].length, 0);
